import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NavigationSteps } from '../../components/OfferDetails/OfferNavigation';
import { AppDispatch, AppThunk } from '../../redux/store';
import { validateFields } from '../../util/validation';
import fetchOfferData from './offerDetailsService';
import { BillingError, Offer, OfferDetails, Prospect, RoomType, State } from './types';

const getOffersAfterStateExclusion = (offers: Offer[], stateCode: string): Offer[] => {
  offers?.forEach((e) => {
    if (e.stateExclusions && e.stateExclusions.length > 0) {
      const excludedStatePresent = e.stateExclusions.some((offerState) => offerState.stateProvince === stateCode);
      if (excludedStatePresent) {
        e.includeOffer = false;
      }
    }
  });
  return offers;
};

const filterOfferOnCustomerSelectedState = (offers: Offer[], stateCode: string): Offer[] => {
  offers?.forEach((e) => {
    if (e.includeOffer) {
      if (e.offerLocation?.stateCode !== stateCode) {
        e.includeOffer = false;
      }
    }
  });
  return offers;
};
const initialState: OfferDetails = {
  loading: false,
  showProspect: false,
  hasOffers: true,
  hasError: false,
  currentNavigationStep: NavigationSteps.OFFER_SEARCH,
  displayFlipOffers: false,
  isPackageSold: false,
};
const offerSlice = createSlice({
  name: 'OfferDetails',
  initialState,
  reducers: {
    receiveOffer(state, action: PayloadAction<OfferDetails>) {
      if (action.payload) {
        const {
          payload: {
            prospect,
            property,
            primaryOffer,
            hasError,
            flipOffers,
            showProspect,
            searchMode,
            searchParameter,
            hasOffers,
            currentOffer,
            locDescription,
            primaryOfferLocations,
            flipOfferLocations,
            error,
            soldPackageInformation,
            isPackageSold,
            alternateLOCDescription,
          },
        } = action;
        if (!state.alternateLOC) {
          state.prospect = prospect;
          if (prospect) {
            state.prospectOriginalState = prospect.stateProvince;
            state.prospectCopy = prospect;
          }
          state.showProspect = showProspect;
          state.locDescription = locDescription;
          state.searchMode = searchMode;
          state.searchParameter = searchParameter;
        } else {
          state.alternateLOCDescription = alternateLOCDescription;
        }

        if (state.alternateLOC) {
          if (!hasError) {
            state.property = property;
            state.primaryOffer = primaryOffer;
            state.flipOffers = flipOffers;

            state.hasOffers = hasOffers;
            state.currentOffer = currentOffer;

            state.primaryOfferLocations = primaryOfferLocations;
            state.flipOfferLocations = flipOfferLocations;
            state.isPackageSold = isPackageSold;
            state.soldPackageInformation = soldPackageInformation;
          }
        } else {
          state.property = property;
          state.primaryOffer = primaryOffer;
          state.flipOffers = flipOffers;

          state.hasOffers = hasOffers;
          state.currentOffer = currentOffer;

          state.primaryOfferLocations = primaryOfferLocations;
          state.flipOfferLocations = flipOfferLocations;
          state.isPackageSold = isPackageSold;
          state.soldPackageInformation = soldPackageInformation;
        }

        state.error = error;
        state.hasError = hasError;
      }
    },
    offerLoading(state: OfferDetails, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    resetSearchMode(state: OfferDetails, action: PayloadAction<string>) {
      state.searchMode = action.payload;
    },
    flipOffer(state: OfferDetails, action: PayloadAction<string>) {
      const selectedOffer = state.flipOffers?.filter((e) => e.offerCode === action.payload)[0];
      state.currentOffer = selectedOffer;
      state.property = selectedOffer?.roomTypes[0].property;
    },
    setSelectedRoom(state: OfferDetails, action: PayloadAction<RoomType>) {
      state.selectedRoomType = action.payload;
    },
    setNavigationStep(state: OfferDetails, action: PayloadAction<NavigationSteps>) {
      state.currentNavigationStep = action.payload;
    },
    setCurrentOffer(state: OfferDetails, action: PayloadAction<number>) {
      state.currentOffer = state.primaryOffer?.filter((e, index) => index === action.payload)[0];
      state.property = state.currentOffer?.property;
    },
    setProspectState(state: OfferDetails, action: PayloadAction<string>) {
      state.prospect = {
        stateProvince: action.payload,
      };
    },
    setProspectNameForLOC(state: OfferDetails, action: PayloadAction<any>) {
      state.prospect = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
      };
    },
    setNewState(state: OfferDetails, action: PayloadAction<string | undefined>) {
      if (state.prospect) state.prospect.stateProvince = action.payload;
    },
    setProspectObjectFromContactInfo(state: OfferDetails, action: PayloadAction<Prospect>) {
      state.prospect = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        spouseFirstName: action.payload.spouseFirstName,
        spouseLastName: action.payload.spouseLastName,
        email: action.payload.email,
        phone: action.payload.phone,
        stateProvince: action.payload.stateProvince,
        addressLine1: action.payload.addressLine1,
        city: action.payload.city,
        postalCode: action.payload.postalCode,
      };
    },
    resetFlipOfferDisplayMode(state: OfferDetails, action: PayloadAction<void>) {
      state.displayFlipOffers = false;
    },
    setFlipOfferDisplayMode(state: OfferDetails, action: PayloadAction<void>) {
      if (state.displayFlipOffers) return;
      state.displayFlipOffers = true;
      const offerLocations = state.primaryOfferLocations;
      /** Union flip offer location and primary offer location */
      state.flipOfferLocations?.forEach((e) => {
        const locationPresent = offerLocations?.some((locationInfo: State) => locationInfo.stateCode === e.stateCode);
        if (!locationPresent) {
          offerLocations?.push(e);
        }
      });
      state.primaryOfferLocations = offerLocations;
    },
    resetCustomerSelectedStates(state: OfferDetails, action: PayloadAction<void>) {
      state.customerExcludedStateCode = undefined;
      state.customerSelectedStateCode = undefined;
    },
    filterByCustomerExcludedState(state: OfferDetails, action: PayloadAction<string>) {
      state.customerExcludedStateCode = action.payload === '-1' ? undefined : action.payload;
      let primaryOffers = state.primaryOffer;
      let flipOffers = state.flipOffers;

      primaryOffers?.forEach((e) => (e.includeOffer = true));
      flipOffers?.forEach((e) => (e.includeOffer = true));

      if (action.payload !== '-1') {
        if (primaryOffers) primaryOffers = getOffersAfterStateExclusion(primaryOffers, action.payload);
        if (flipOffers) flipOffers = getOffersAfterStateExclusion(flipOffers, action.payload);
      }
      if (state.customerSelectedStateCode) {
        if (primaryOffers)
          primaryOffers = filterOfferOnCustomerSelectedState(primaryOffers, state.customerSelectedStateCode);
        if (flipOffers) flipOffers = filterOfferOnCustomerSelectedState(flipOffers, state.customerSelectedStateCode);
      }
      state.primaryOffer = primaryOffers;
      state.flipOffers = flipOffers;
    },
    filterByCustomerSelectedState(state: OfferDetails, action: PayloadAction<string>) {
      state.customerSelectedStateCode = action.payload === '-1' ? undefined : action.payload;
      let primaryOffers = state.primaryOffer;
      let flipOffers = state.flipOffers;
      primaryOffers?.forEach((e) => (e.includeOffer = true));
      flipOffers?.forEach((e) => (e.includeOffer = true));
      if (state.customerExcludedStateCode) {
        if (primaryOffers) primaryOffers = getOffersAfterStateExclusion(primaryOffers, state.customerExcludedStateCode);
        if (flipOffers) flipOffers = getOffersAfterStateExclusion(flipOffers, state.customerExcludedStateCode);
      }
      if (action.payload !== '-1') {
        if (primaryOffers) primaryOffers = filterOfferOnCustomerSelectedState(primaryOffers, action.payload);
        if (flipOffers) flipOffers = filterOfferOnCustomerSelectedState(flipOffers, action.payload);
      }

      state.primaryOffer = primaryOffers;
      state.flipOffers = flipOffers;
    },
    setGuests(state: OfferDetails, action: PayloadAction<any>) {
      state.guests = {
        adults: action.payload.adults,
        children: action.payload.children,
      };
    },
    resetSoldPackage(state: OfferDetails, action: PayloadAction<void>) {
      state.isPackageSold = false;
    },
    resetGuestsAndState(state: OfferDetails, action: PayloadAction<void>) {
      state.guests = {
        adults: 1,
        children: 0,
      };
    },
    setAlternateLOC(state: OfferDetails, action: PayloadAction<string>) {
      state.alternateLOC = action.payload;
    },
    resetAlternateLOC(state: OfferDetails, action: PayloadAction<void>) {
      state.alternateLOC = undefined;
      state.alternateLOCDescription = undefined;
    },
    setBillingObjectFromContactInfo(state: OfferDetails, action: PayloadAction<Prospect>) {
      state.billingInfo = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        phone: action.payload.phone,
        stateProvince: action.payload.stateProvince,
        addressLine1: action.payload.addressLine1,
        city: action.payload.city,
        postalCode: action.payload.postalCode,
        unitNo: action.payload.unitNo,
      };
    },
    setBillingErrorfromContactInfo(state: OfferDetails, action: PayloadAction<BillingError>) {
      state.billingError = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        phone: action.payload.phone,
        stateProvince: action.payload.stateProvince,
        addressLine1: action.payload.addressLine1,
        city: action.payload.city,
        postalCode: action.payload.postalCode,
      };
    },
    setProspectCopyObjectFromContactInfo(state: OfferDetails, action: PayloadAction<Prospect>) {
      state.prospectCopy = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        phone: action.payload.phone,
        stateProvince: action.payload.stateProvince,
        addressLine1: action.payload.addressLine1,
        city: action.payload.city,
        postalCode: action.payload.postalCode,
        spouseFirstName: action.payload.spouseFirstName,
        spouseLastName: action.payload.spouseLastName,
        unitNo: action.payload.unitNo,
      };
    },
    setBillingInfoIfequal(state: OfferDetails, action: PayloadAction<boolean>) {
      state.billingInfo = state.prospect;
      state.billingBoxChecked = action.payload;
      state.billingError = {
        firstName: state.prospect?.firstName ? validateFields('firstName', state.prospect?.firstName) : '',
        lastName: state.prospect?.lastName ? validateFields('lastName', state.prospect?.lastName) : '',
        email: state.prospect?.email ? validateFields('email', state.prospect?.email) : '',
        phone: state.prospect?.phone ? validateFields('phone', state.prospect?.phone) : '',
        stateProvince: state.prospect?.stateProvince
          ? validateFields('stateProvince', state.prospect?.stateProvince)
          : '',
        addressLine1: state.prospect?.addressLine1 ? validateFields('addressLine1', state.prospect?.addressLine1) : '',
        city: state.prospect?.city ? validateFields('city', state.prospect?.city) : '',
        postalCode: state.prospect?.postalCode ? validateFields('postalCode', state.prospect?.postalCode) : '',
      };
    },
    setContactErrorfromContactInfo(state: OfferDetails, action: PayloadAction<BillingError>) {
      state.contactError = {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        phone: action.payload.phone,
        addressLine1: action.payload.addressLine1,
        city: action.payload.city,
        postalCode: action.payload.postalCode,
      };
    },
    resetBillingInfo(state: OfferDetails, action: PayloadAction<void>) {
      state.billingInfo = undefined;
      state.billingBoxChecked = false;
      state.billingInfoChecked = true;
      state.billingError = undefined;
    },
    resetAllContactBillingInfo(state: OfferDetails, action: PayloadAction<void>) {
      state.billingInfo = undefined;
      state.billingBoxChecked = false;
      state.billingInfoChecked = true;
      state.contactInfoChecked = true;
      state.billingError = undefined;
      state.contactError = undefined;
    },
    resetBillingCheckBox(state: OfferDetails, action: PayloadAction<void>) {
      state.billingBoxChecked = false;
    },
    setAllContactInfoFields(state: OfferDetails, action: PayloadAction<boolean>) {
      state.contactInfoChecked = action.payload;
    },
    setAllBillingInfoFields(state: OfferDetails, action: PayloadAction<boolean>) {
      state.billingInfoChecked = action.payload;
    },
    setRadioButtonValue(state: OfferDetails, action: PayloadAction<string>) {
      state.conversationMode = action.payload;
    },
    setEffortCodeValue(state: OfferDetails, action: PayloadAction<string>) {
      state.effortCode = action.payload;
    },
    resetEffortCode(state: OfferDetails, action: PayloadAction<void>) {
      state.effortCode = undefined;
    },
    setBillingObjectState(state: OfferDetails, action: PayloadAction<string>) {
      state.billingInfo = {
        firstName: state.billingInfo?.firstName,
        lastName: state.billingInfo?.lastName,
        email: state.billingInfo?.email,
        phone: state.billingInfo?.phone,
        stateProvince: action.payload,
        addressLine1: state.billingInfo?.addressLine1,
        city: state.billingInfo?.city,
        postalCode: state.billingInfo?.postalCode,
        unitNo: state.billingInfo?.unitNo,
      };
    },
  },
});
export const {
  setProspectState,
  resetSearchMode,
  flipOffer,
  setSelectedRoom,
  setNavigationStep,
  setCurrentOffer,
  setProspectObjectFromContactInfo,
  setFlipOfferDisplayMode,
  filterByCustomerExcludedState,
  filterByCustomerSelectedState,
  resetFlipOfferDisplayMode,
  resetCustomerSelectedStates,
  setGuests,
  resetGuestsAndState,
  setNewState,
  resetSoldPackage,
  setProspectNameForLOC,
  setAlternateLOC,
  resetAlternateLOC,
  setBillingInfoIfequal,
  resetBillingInfo,
  setBillingObjectFromContactInfo,
  setProspectCopyObjectFromContactInfo,
  resetBillingCheckBox,
  setAllContactInfoFields,
  setAllBillingInfoFields,
  resetAllContactBillingInfo,
  setRadioButtonValue,
  setBillingObjectState,
  setBillingErrorfromContactInfo,
  setEffortCodeValue,
  resetEffortCode,
  setContactErrorfromContactInfo,
} = offerSlice.actions;
export const loadOfferDetails = (
  pin: string,
  mode: string,
  alternateLOCFlag: string | undefined = undefined,
  customerExcludedStateCode: string | undefined = undefined,
  customerSelectedStateCode: string | undefined = undefined,
  effortCode: string | undefined = undefined,
): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(offerSlice.actions.offerLoading(true));
  dispatch(resetCustomerSelectedStates());

  if (alternateLOCFlag) {
    dispatch(offerSlice.actions.setAlternateLOC(pin));
    dispatch(offerSlice.actions.resetFlipOfferDisplayMode());
  } else {
    dispatch(offerSlice.actions.resetAlternateLOC());
    dispatch(offerSlice.actions.resetCustomerSelectedStates());
  }
  const offerDetails: OfferDetails = await fetchOfferData(pin, mode, alternateLOCFlag, effortCode);

  dispatch(offerSlice.actions.receiveOffer(offerDetails));
  if (alternateLOCFlag) {
    if (customerExcludedStateCode)
      dispatch(offerSlice.actions.filterByCustomerExcludedState(customerExcludedStateCode));
    if (customerSelectedStateCode)
      dispatch(offerSlice.actions.filterByCustomerSelectedState(customerSelectedStateCode));
  }

  dispatch(offerSlice.actions.offerLoading(false));
};
export default offerSlice.reducer;
